import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useClasses } from '../hooks/useClasses';
import { useStudents } from '../hooks/useStudents';
import { useEnrollments } from '../hooks/useEnrollments';
import { 
  BookOpen, 
  Clock, 
  Users, 
  CheckCircle, 
  Calendar,
  MessageSquare,
  Bell,
  AlertCircle,
  ChevronRight,
  Send,
  ClipboardCheck,
  GraduationCap,
  Mail,
  Loader2,
  X,
  CheckSquare
} from 'lucide-react';

const EmployeePortal = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  // Modals state
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedClassForAttendance, setSelectedClassForAttendance] = useState(null);
  const [announcementText, setAnnouncementText] = useState('');
  const [announcementClass, setAnnouncementClass] = useState('all');

  // Fetch real data
  const { data: classesData, isLoading: classesLoading, error: classesError } = useClasses();
  const { data: studentsData, isLoading: studentsLoading } = useStudents();
  const { data: enrollmentsData, isLoading: enrollmentsLoading } = useEnrollments();

  // Get employee's assigned classes
  const myClasses = useMemo(() => {
    if (!classesData?.classes || !user?.id) return [];
    return classesData.classes.filter(c => c.instructor_id === user.id);
  }, [classesData, user]);

  // Get students in employee's classes
  const myClassIds = useMemo(() => myClasses.map(c => c.id), [myClasses]);
  
  const myEnrollments = useMemo(() => {
    if (!enrollmentsData?.enrollments) return [];
    return enrollmentsData.enrollments.filter(e => myClassIds.includes(e.class_id));
  }, [enrollmentsData, myClassIds]);

  const myStudents = useMemo(() => {
    if (!studentsData?.students || !myEnrollments.length) return [];
    const enrolledStudentIds = myEnrollments.map(e => e.student_id);
    return studentsData.students.filter(s => enrolledStudentIds.includes(s.id));
  }, [studentsData, myEnrollments]);

  // Calculate today's schedule based on class schedules
  const todaySchedule = useMemo(() => {
    const today = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const todayName = dayNames[today.getDay()];
    
    return myClasses
      .filter(cls => {
        // Check if class has schedule for today
        if (!cls.schedule) return false;
        if (typeof cls.schedule === 'string') {
          return cls.schedule.toLowerCase().includes(todayName.substring(0, 3));
        }
        if (Array.isArray(cls.schedule)) {
          return cls.schedule.some(s => s.day?.toLowerCase() === todayName);
        }
        return false;
      })
      .map(cls => {
        let time = 'TBD';
        if (Array.isArray(cls.schedule)) {
          const todaySlot = cls.schedule.find(s => {
            const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            return s.day?.toLowerCase() === dayNames[new Date().getDay()];
          });
          if (todaySlot) {
            time = todaySlot.startTime || 'TBD';
          }
        } else if (typeof cls.schedule === 'string') {
          // Try to extract time from string like "Mon & Wed, 4:00 PM - 5:00 PM"
          const timeMatch = cls.schedule.match(/(\d{1,2}:\d{2}\s*(?:AM|PM)?)/i);
          if (timeMatch) time = timeMatch[1];
        }
        
        // Determine if class is upcoming or completed based on current time
        const now = new Date();
        const currentHour = now.getHours();
        let status = 'upcoming';
        
        if (time !== 'TBD') {
          const hourMatch = time.match(/(\d{1,2})/);
          if (hourMatch) {
            const classHour = parseInt(hourMatch[1]);
            const isPM = time.toLowerCase().includes('pm');
            const adjustedClassHour = isPM && classHour !== 12 ? classHour + 12 : classHour;
            status = currentHour >= adjustedClassHour + 1 ? 'completed' : 'upcoming';
          }
        }
        
        return {
          id: cls.id,
          time,
          class: cls.name,
          status,
          location: cls.location || 'Main Studio'
        };
      })
      .sort((a, b) => {
        // Sort by time
        if (a.time === 'TBD') return 1;
        if (b.time === 'TBD') return -1;
        return a.time.localeCompare(b.time);
      });
  }, [myClasses]);

  // Stats
  const totalStudents = myStudents.length;
  const classesToday = todaySchedule.length;
  const pendingAttendance = myClasses.length; // Simplified - would calculate based on actual attendance records
  const unreadMessages = 3; // Placeholder - would fetch from messages table

  // Recent attendance data (mock for now - would fetch from attendance service)
  const recentAttendance = useMemo(() => {
    return myStudents.slice(0, 5).map(student => ({
      student: `${student.first_name} ${student.last_name}`,
      class: myClasses.find(c => c.id === myEnrollments.find(e => e.student_id === student.id)?.class_id)?.name || 'Unknown',
      status: Math.random() > 0.2 ? 'present' : Math.random() > 0.5 ? 'tardy' : 'absent',
      date: new Date().toLocaleDateString()
    }));
  }, [myStudents, myClasses, myEnrollments]);

  // Pending tasks
  const pendingTasks = [
    { id: 1, title: 'Submit attendance for Ballet Beginners', priority: 'high', due: 'Today' },
    { id: 2, title: 'Review new enrollment applications', priority: 'medium', due: 'Tomorrow' },
    { id: 3, title: 'Update lesson plans for next week', priority: 'low', due: 'This Week' },
  ];

  // Recent messages
  const recentMessages = [
    { id: 1, from: 'Sarah Johnson', subject: 'Emma will be absent tomorrow', time: '2 hours ago', unread: true },
    { id: 2, from: 'Michael Chen', subject: 'Question about gymnastics schedule', time: '5 hours ago', unread: true },
    { id: 3, from: 'Admin', subject: 'Staff meeting on Friday', time: '1 day ago', unread: false },
  ];

  // Loading state
  if (classesLoading || studentsLoading || enrollmentsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-purple-600" size={32} />
      </div>
    );
  }

  // Error state
  if (classesError) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
        Failed to load data: {classesError.message}
      </div>
    );
  }

  const handleTakeAttendance = (classId) => {
    navigate('/employee/attendance', { state: { selectedClass: classId } });
  };

  const handleMarkAllPresent = (classId) => {
    setSelectedClassForAttendance(classId);
    setShowAttendanceModal(true);
  };

  const handleSendAnnouncement = () => {
    setShowAnnouncementModal(true);
  };

  const handleSubmitAnnouncement = (e) => {
    e.preventDefault();
    // Would send announcement to API
    console.log('Sending announcement:', { text: announcementText, class: announcementClass });
    setShowAnnouncementModal(false);
    setAnnouncementText('');
  };

  const handleConfirmMarkAllPresent = () => {
    // Would mark all students as present via API
    console.log('Marking all present for class:', selectedClassForAttendance);
    setShowAttendanceModal(false);
    setSelectedClassForAttendance(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':
        return <CheckCircle className="text-green-500" size={16} />;
      case 'absent':
        return <AlertCircle className="text-red-500" size={16} />;
      case 'tardy':
        return <Clock className="text-yellow-500" size={16} />;
      default:
        return <Clock className="text-gray-400" size={16} />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      present: 'bg-green-100 text-green-700',
      absent: 'bg-red-100 text-red-700',
      tardy: 'bg-yellow-100 text-yellow-700',
    };
    return <span className={`text-xs px-2 py-1 rounded-full ${styles[status]}`}>{status}</span>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-red-100 text-red-700',
      medium: 'bg-yellow-100 text-yellow-700',
      low: 'bg-gray-100 text-gray-700',
    };
    return <span className={`text-xs px-2 py-1 rounded-full ${styles[priority]}`}>{priority}</span>;
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Hello, {user?.firstName || user?.email?.split('@')[0] || 'Teacher'}!
        </h1>
        <p className="text-gray-500 mt-1">
          Here's your schedule and class information for today.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-purple-100">
          <div className="flex items-center justify-between mb-2">
            <Users className="text-purple-600" size={20} />
            <span className="text-xs text-gray-400">Total</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalStudents}</p>
          <p className="text-sm text-gray-500">Students You Teach</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-purple-100">
          <div className="flex items-center justify-between mb-2">
            <Calendar className="text-fuchsia-600" size={20} />
            <span className="text-xs text-gray-400">Today</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{classesToday}</p>
          <p className="text-sm text-gray-500">Classes Today</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-purple-100">
          <div className="flex items-center justify-between mb-2">
            <ClipboardCheck className="text-blue-600" size={20} />
            <span className="text-xs text-gray-400">Pending</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{pendingAttendance}</p>
          <p className="text-sm text-gray-500">Attendance Records</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-purple-100">
          <div className="flex items-center justify-between mb-2">
            <Mail className="text-green-600" size={20} />
            <span className="text-xs text-gray-400">New</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{unreadMessages}</p>
          <p className="text-sm text-gray-500">Unread Messages</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-8">
          {/* Today's Schedule */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Today's Schedule</h2>
              <button 
                onClick={() => navigate('/employee/classes')}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium"
              >
                View All Classes
              </button>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-purple-100">
              {todaySchedule.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Calendar className="mx-auto mb-2 text-gray-300" size={32} />
                  <p>No classes scheduled for today</p>
                </div>
              ) : (
                todaySchedule.map((item, index) => (
                  <div
                    key={item.id}
                    className={`p-4 ${index !== todaySchedule.length - 1 ? 'border-b border-purple-50' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          item.status === 'completed' ? 'bg-green-100' : 'bg-purple-100'
                        }`}>
                          {item.status === 'completed' ? (
                            <CheckCircle className="text-green-600" size={24} />
                          ) : (
                            <Clock className="text-purple-600" size={24} />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{item.class}</p>
                          <p className="text-sm text-gray-500">{item.time} • {item.location}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {item.status === 'upcoming' && (
                          <button
                            onClick={() => handleTakeAttendance(item.id)}
                            className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                          >
                            Take Attendance
                          </button>
                        )}
                        <span className={`badge ${
                          item.status === 'completed' ? 'badge-success' : 'badge-info'
                        }`}>
                          {item.status === 'completed' ? 'Completed' : 'Upcoming'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* My Classes */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">My Classes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {myClasses.length === 0 ? (
                <div className="col-span-full p-8 text-center bg-white rounded-xl border border-purple-100 text-gray-500">
                  <BookOpen className="mx-auto mb-2 text-gray-300" size={32} />
                  <p>No classes assigned to you yet</p>
                </div>
              ) : (
                myClasses.map((classItem) => {
                  const enrollmentCount = myEnrollments.filter(e => e.class_id === classItem.id).length;
                  return (
                    <div key={classItem.id} className="bg-white rounded-xl p-6 shadow-sm border border-purple-100">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 bg-fuchsia-100 rounded-lg flex items-center justify-center">
                          <BookOpen className="text-fuchsia-600" size={24} />
                        </div>
                        <span className="badge badge-info">{classItem.status || 'Active'}</span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{classItem.name}</h3>
                      <p className="text-gray-500 mb-4 text-sm">
                        {Array.isArray(classItem.schedule) 
                          ? classItem.schedule.map(s => `${s.day} ${s.startTime}`).join(', ')
                          : classItem.schedule || 'Schedule TBD'
                        }
                      </p>
                      
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-2">
                          <Users size={16} className="text-gray-400" />
                          <span className="text-gray-600">
                            {enrollmentCount}/{classItem.capacity} students
                          </span>
                        </div>
                        <span className="text-gray-500">{classItem.location || 'Main Studio'}</span>
                      </div>

                      <div className="mt-4 pt-4 border-t border-purple-50 grid grid-cols-2 gap-2">
                        <button 
                          onClick={() => handleTakeAttendance(classItem.id)}
                          className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                        >
                          Take Attendance
                        </button>
                        <button 
                          onClick={() => handleMarkAllPresent(classItem.id)}
                          className="px-3 py-2 bg-white text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition-colors text-sm font-medium"
                        >
                          Mark All Present
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Students in My Classes */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Students in My Classes</h2>
            <div className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden">
              {myStudents.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <GraduationCap className="mx-auto mb-2 text-gray-300" size={32} />
                  <p>No students enrolled in your classes yet</p>
                </div>
              ) : (
                <div className="divide-y divide-purple-50">
                  {myStudents.slice(0, 6).map((student) => {
                    const enrollment = myEnrollments.find(e => e.student_id === student.id);
                    const className = myClasses.find(c => c.id === enrollment?.class_id)?.name;
                    return (
                      <div key={student.id} className="p-4 flex items-center justify-between hover:bg-purple-50/50">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-purple-600 font-medium">
                              {student.first_name?.[0]}{student.last_name?.[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{student.first_name} {student.last_name}</p>
                            <p className="text-sm text-gray-500">{className}</p>
                          </div>
                        </div>
                        <button className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                          View Profile
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {myStudents.length > 6 && (
                <div className="p-4 text-center border-t border-purple-50">
                  <button className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                    View All {myStudents.length} Students
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button 
                onClick={() => navigate('/employee/attendance')}
                className="p-4 bg-white rounded-xl shadow-sm border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all text-center"
              >
                <Clock className="mx-auto mb-2 text-purple-600" size={24} />
                <span className="text-sm font-medium text-gray-700">Clock In/Out</span>
              </button>
              <button 
                onClick={() => navigate('/employee/attendance')}
                className="p-4 bg-white rounded-xl shadow-sm border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all text-center"
              >
                <CheckCircle className="mx-auto mb-2 text-purple-600" size={24} />
                <span className="text-sm font-medium text-gray-700">Attendance</span>
              </button>
              <button 
                onClick={() => navigate('/employee/communications')}
                className="p-4 bg-white rounded-xl shadow-sm border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all text-center"
              >
                <Users className="mx-auto mb-2 text-purple-600" size={24} />
                <span className="text-sm font-medium text-gray-700">Message Parents</span>
              </button>
              <button 
                onClick={handleSendAnnouncement}
                className="p-4 bg-white rounded-xl shadow-sm border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all text-center"
              >
                <Send className="mx-auto mb-2 text-purple-600" size={24} />
                <span className="text-sm font-medium text-gray-700">Class Announcement</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-8">
          {/* Pending Tasks */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Pending Tasks</h2>
              <Bell className="text-gray-400" size={20} />
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden">
              <div className="divide-y divide-purple-50">
                {pendingTasks.map((task) => (
                  <div key={task.id} className="p-4 hover:bg-purple-50/50">
                    <div className="flex items-start justify-between mb-1">
                      <p className="font-medium text-gray-900 text-sm">{task.title}</p>
                      {getPriorityBadge(task.priority)}
                    </div>
                    <p className="text-xs text-gray-500">Due: {task.due}</p>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-purple-50 text-center">
                <button className="text-sm text-purple-600 hover:text-purple-700 font-medium">
                  View All Tasks
                </button>
              </div>
            </div>
          </div>

          {/* Recent Attendance */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Attendance</h2>
              <button 
                onClick={() => navigate('/employee/attendance')}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium"
              >
                View All
              </button>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden">
              {recentAttendance.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No recent attendance records
                </div>
              ) : (
                <div className="divide-y divide-purple-50">
                  {recentAttendance.map((record, index) => (
                    <div key={index} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{record.student}</p>
                        <p className="text-xs text-gray-500">{record.class}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(record.status)}
                        {getStatusBadge(record.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Messages */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
              <button 
                onClick={() => navigate('/employee/communications')}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center"
              >
                View All <ChevronRight size={16} />
              </button>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden">
              <div className="divide-y divide-purple-50">
                {recentMessages.map((message) => (
                  <div 
                    key={message.id} 
                    className={`p-4 hover:bg-purple-50/50 cursor-pointer ${message.unread ? 'bg-purple-50/30' : ''}`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`w-2 h-2 rounded-full mt-2 ${message.unread ? 'bg-purple-600' : 'bg-transparent'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm">{message.from}</p>
                        <p className="text-sm text-gray-600 truncate">{message.subject}</p>
                        <p className="text-xs text-gray-400 mt-1">{message.time}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-purple-50 text-center">
                <button 
                  onClick={() => navigate('/employee/communications')}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center justify-center w-full"
                >
                  <MessageSquare size={16} className="mr-2" />
                  Compose Message
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Announcement Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Send Class Announcement</h2>
              <button 
                onClick={() => setShowAnnouncementModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmitAnnouncement} className="space-y-4">
              <div>
                <label className="form-label">To</label>
                <select 
                  className="form-input"
                  value={announcementClass}
                  onChange={(e) => setAnnouncementClass(e.target.value)}
                >
                  <option value="all">All My Classes</option>
                  {myClasses.map(cls => (
                    <option key={cls.id} value={cls.id}>{cls.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Message</label>
                <textarea 
                  className="form-input h-32" 
                  placeholder="Type your announcement..."
                  value={announcementText}
                  onChange={(e) => setAnnouncementText(e.target.value)}
                  required
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAnnouncementModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary flex items-center justify-center space-x-2"
                >
                  <Send size={16} />
                  <span>Send Announcement</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mark All Present Modal */}
      {showAttendanceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Mark All Present</h2>
              <button 
                onClick={() => setShowAttendanceModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckSquare className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-gray-900">
                  Mark all students in <strong>
                    {myClasses.find(c => c.id === selectedClassForAttendance)?.name}
                  </strong> as present?
                </p>
                <p className="text-sm text-gray-500 mt-1">This will mark attendance for today.</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowAttendanceModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmMarkAllPresent}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeePortal;
